import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import { TypedMemo } from '../../common/types';
import { reviewMessages } from './reviewMessages';
import { bodyLength, problemOf, REVIEW_LIMITS, ReviewDraft, ReviewProblem } from './reviewRules';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the review screen; each part uses its own slice of it */
import styles from './Review.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReviewTextFieldsProps {
  draft: ReviewDraft;
  onChange: (patch: Partial<ReviewDraft>) => void;
  problems: ReviewProblem[];
  disabled: boolean;
}

/** The optional title and the review text with its character counter (design review-form a1, a2). */
const ReviewTextFieldsComponent: React.FC<ReviewTextFieldsProps> = ({ draft, onChange, problems, disabled }) => {
  const intl = useIntl();
  const titleProblem = problemOf(problems, 'title');
  const bodyProblem = problemOf(problems, 'body');
  const length = bodyLength(draft.body);

  const bodyLine = () => {
    if (bodyProblem?.kind === 'tooShort') {
      return intl.formatMessage(reviewMessages.bodyTooShort, {
        min: REVIEW_LIMITS.bodyMin,
        missing: bodyProblem.missing,
      });
    }
    if (bodyProblem?.kind === 'tooLong') {
      return intl.formatMessage(reviewMessages.bodyTooLong, { max: REVIEW_LIMITS.bodyMax });
    }
    return intl.formatMessage(reviewMessages.bodyHint, { min: REVIEW_LIMITS.bodyMin });
  };

  return (
    <>
      <FormGroup className="mb-0">
        <Label for="reviewTitle">
          {intl.formatMessage(reviewMessages.titleLabel)}{' '}
          <span className="text-body-secondary">{intl.formatMessage(reviewMessages.titleOptional)}</span>
        </Label>
        <Input
          id="reviewTitle"
          value={draft.title}
          maxLength={REVIEW_LIMITS.titleMax}
          invalid={titleProblem !== undefined}
          placeholder={intl.formatMessage(reviewMessages.titlePlaceholder)}
          disabled={disabled}
          onChange={(event) => onChange({ title: event.target.value })}
        />
        {titleProblem !== undefined && (
          <FormFeedback>
            {intl.formatMessage(reviewMessages.titleTooLong, { max: REVIEW_LIMITS.titleMax })}
          </FormFeedback>
        )}
      </FormGroup>
      <FormGroup className="mb-0">
        <Label for="reviewBody">{intl.formatMessage(reviewMessages.bodyLabel)}</Label>
        <Input
          id="reviewBody"
          type="textarea"
          className={styles.body}
          value={draft.body}
          maxLength={REVIEW_LIMITS.bodyMax}
          invalid={bodyProblem !== undefined}
          placeholder={intl.formatMessage(reviewMessages.bodyPlaceholder)}
          disabled={disabled}
          aria-describedby="reviewBodyLine reviewBodyCounter"
          onChange={(event) => onChange({ body: event.target.value })}
        />
        <div className="d-flex justify-content-between gap-3 mt-1">
          <span id="reviewBodyLine" className={bodyProblem !== undefined ? styles.error : styles.hint}>
            {bodyLine()}
          </span>
          <span
            id="reviewBodyCounter"
            className={clsx('fig', styles.hint, bodyProblem !== undefined && styles.counterBad)}
            data-testid="review-body-counter"
          >
            {intl.formatMessage(reviewMessages.bodyCounter, { count: length, max: REVIEW_LIMITS.bodyMax })}
          </span>
        </div>
      </FormGroup>
    </>
  );
};

export const ReviewTextFields = TypedMemo(ReviewTextFieldsComponent);
